<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'dashboard',
    path: '/',
    component: 'Dashboard',
    default: true,
    title: () => gettext('Dashboard'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Dashboard',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const config = ref(null);
  const notifications = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));

  const $data = computed(() => ({
    config: config.value,
    notifications: notifications.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_config = async () => {
    return $fn.value.fetch('get_ui_dashboard_config').then((cfg) => {
      const items = _.sortBy(cfg.items || [], 'pos');
      const nCols = _.max(_.flatMap(items, (i) => [i.col, i.colspan])) || 1;

      return Promise.all(
        items.map((item) => {
          const cell = {
            ...item,
            props: item.props || {},
            bsSpan: _.clamp(_.round((item.colspan * 12) / nCols), 1, 12),
          };
          return cell.props.id == null
            ? cell
            : $fn.value
                .fetch('get_superset_dashboard_token', {
                  parameters: { id: cell.props.id },
                })
                .then(
                  (resp) => ({ ...cell, url: resp.url }),
                  () => ({ ...cell, url: null })
                );
        })
      ).then((items) => ({ items }));
    });
  };
  const $$load_notifications = async () => {
    return $fn.value
      .fetch('list_notification', { parameters: { state: 'new' } })
      .then((p) => _.orderBy(p, ['created_at'], ['desc']))
      .catch(() => []);
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'config' },
      { name: 'notifications' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { config: $$load_config, notifications: $$load_notifications },
    ux: {},
    dataRefs: {
      config,
      notifications,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: $fn.value.gettext('Dashboard'),
        icon: $config.value.dashboard.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_hb20h6vlk_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_hb20h6vlk =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_hb20h6vlk_getActionArgs,
    });
  const inst_evh_7315092382398562_close_06mh09aov =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_hb20h6vlk,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_06mh09aov],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_a87789hst =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_5poopz3hs =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_a87789hst,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_5poopz3hs],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_06mh09aov,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_5poopz3hs,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_close_06mh09aov = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_06mh09aov.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_5poopz3hs = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_5poopz3hs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Dashboard"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <div class="row g-3">
        <div
          :key="index"
          :class="'col-12 col-xl-' + item.bsSpan"
          v-for="(item, index) in config.items"
        >
          <Notifications
            :items="notifications"
            :panelHeight="item.props.height"
            v-if="item.name === 'Notifications'"
          >
          </Notifications>
          <UcDashboardChart
            :url="item.url"
            :dashboardId="item.props.id"
            :title="item.props.title"
            :height="item.props.height"
            v-if="item.props.id != null"
          >
          </UcDashboardChart>
        </div>
        <div class="col-12" v-if="!config.items.length">
          <div class="card">
            <div class="card-body text-muted text-center py-5">
              {{ gettext('No dashboard widgets are configured yet.') }}
            </div>
          </div>
        </div>
      </div>
    </UcBody>
  </ItpPage>
</template>
